import {
  PERCENTAGE_OFF,
  FIXED_AMOUNT_OFF,
  PROMO_APPLY_SCOPE_REPORT_GROUP,
} from "@/config/const";
import moment from "moment";

/**
 * M977: subtotal of the cart lines a report-group promo applies to.
 * Mirrors MintyPromoCode::eligibleSubtotal() in minty.crm.onlineordering.api —
 * a line counts when its main item's group_id is one of the promo's report groups,
 * with its modifiers (price_default / member_price) or without them (*_no_modifier_option).
 */
export const promoEligibleSubtotal = (promo, items, useMemberPrice) => {
  const groupIds = (promo?.report_group_ids ?? []).map(Number);

  return (items ?? []).reduce((total, line) => {
    if (!line || line.is_free_item || line.is_delivery_fee_item) {
      return total;
    }
    const quantity = Number(line.quantity) || 0;
    if (quantity <= 0 || !groupIds.includes(Number(line.group_id))) {
      return total;
    }

    const withModifiers = useMemberPrice ? line.member_price : line.price_default;
    const mainOnly = useMemberPrice
      ? line.member_price_no_modifier_option
      : line.price_default_no_modifier_option;
    const price = Number(promo.include_modifiers) === 1 ? withModifiers : mainOnly ?? withModifiers;

    return total + (parseFloat(price) || 0) * quantity;
  }, 0);
};

/**
 * Discount of the applied promo code (unrounded).
 * Whole bill: percentage of billSubtotal, or the fixed amount.
 * Report groups (M977): percentage of the eligible items, or the fixed amount capped at them.
 */
export const promoDiscountAmount = (promo, billSubtotal, items, useMemberPrice) => {
  if (!promo) {
    return 0;
  }

  const isReportGroup = Number(promo.apply_scope) === PROMO_APPLY_SCOPE_REPORT_GROUP;
  const base = isReportGroup
    ? promoEligibleSubtotal(promo, items, useMemberPrice)
    : billSubtotal;
  const value = parseFloat(promo.discount_value) || 0;

  if (promo.discount_type === PERCENTAGE_OFF) {
    return base * (value / 100);
  }
  if (promo.discount_type === FIXED_AMOUNT_OFF) {
    return isReportGroup ? Math.min(value, base) : value;
  }

  return 0;
};

// reason_code values the API sends (MintyPromoCode::REASON_*), each translated under promoReasons.
const PROMO_REASONS = [
  "not_found", "disabled", "expired", "used", "reserved", "out_of_stock", "wrong_branch",
  "not_assigned", "unavailable", "not_applicable", "not_in_schedule", "daily_limit",
];

/**
 * Why a promo code was refused, in the customer's language: from the response's reason_code
 * (and next_at, "YYYY-MM-DD HH:mm" Singapore time, for a schedule), else the API's English message.
 */
export const promoReasonText = (t, data, fallback) => {
  const code = data?.reason_code;
  if (!PROMO_REASONS.includes(code)) {
    return fallback;
  }
  if (code === "not_in_schedule" && data.next_at) {
    return t("promoReasons.not_in_schedule_next", { next: moment(data.next_at, "YYYY-MM-DD HH:mm").format("DD/MM/YYYY HH:mm") });
  }
  return t(`promoReasons.${code}`);
};

const escapeHtml = (text) =>
  String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);

/**
 * Body of the modal shown when the applied promo code can no longer be used: a title naming the
 * code, the reason from the API on its own line, and a short note. ModalNotification renders it
 * with v-html, so every text is escaped.
 */
export const promoRemovedMessage = ({ title, reason, note }) =>
  `<div class="fw-bolder fs-5 mb-2">${escapeHtml(title)}</div>` +
  (reason ? `<div class="fw-normal mb-2">${escapeHtml(reason)}</div>` : "") +
  `<div class="fw-normal small text-secondary">${escapeHtml(note)}</div>`;
